import ErrorBoundary from '@components/error-boundary';
import Layout from '@components/layout';
import ProtectedRoute from '@components/protected-route';
import RoleGuard from '@components/role-guard';
import ManageUsers from '@features/app-users/manage-users';
import AuditLogs from '@features/audit-logs/audit-logs';
import ChangePassword from '@features/auth/change-password';
import CreateAccount from '@features/auth/create-account';
import Login from '@features/auth/login';
import Categories from '@features/categories/categories';
import ManageCategories from '@features/categories/manage-categories';
import EditionHallOfFame from '@features/editions/edition-hall-of-fame';
import EditionNominees from '@features/editions/edition-nominees';
import EditionVotes from '@features/editions/edition-votes';
import Editions from '@features/editions/editions';
import Nominees from '@features/nominees/nominees';
import ManagePmsVoters from '@features/pms-voters/manage-pms-voters';
import RegularUsers from '@features/regular-users/regular-users';
import ManageSites from '@features/sites/manage-sites';
import Votes from '@features/votes/votes';
import HallOfFame from '@features/voting-simulation/hall-of-fame';
import VotingDashboard from '@features/voting-simulation/voting-dashboard';
import VotingLogin from '@features/voting-simulation/voting-login';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sileo';

export default function App(): React.JSX.Element {
  return (
    <ErrorBoundary>
      <Toaster position="bottom-center" options={{ fill: '#18181b' }} />
      <Routes>
        {/* Temporary voting simulation — remove when no longer needed */}
        <Route path="/voting" element={<VotingLogin />} />
        <Route path="/voting/vote" element={<VotingDashboard />} />
        <Route path="/voting/hall-of-fame" element={<HallOfFame />} />

        <Route element={<Layout />}>
          <Route index element={<Login />} />
          <Route path="/login" element={<Login />} />
          <Route path="/create-account" element={<CreateAccount />} />
          <Route element={<ProtectedRoute />}>
            {/* Reachable while a password change is pending; everything else is not. */}
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/editions" element={<Editions />} />
            <Route path="/editions/:editionId/votes" element={<EditionVotes />} />
            <Route path="/editions/:editionId/nominees" element={<EditionNominees />} />
            <Route path="/editions/:editionId/hall-of-fame" element={<EditionHallOfFame />} />
            <Route path="/editions/:editionId/categories" element={<Categories />} />
            <Route
              path="/editions/:editionId/categories/:categoryId/nominees"
              element={<Nominees />}
            />
            <Route path="/editions/:editionId/categories/:categoryId/votes" element={<Votes />} />

            {/* Admin+ only routes */}
            <Route element={<RoleGuard allowed={['superadmin', 'admin']} />}>
              <Route path="/categories" element={<ManageCategories />} />
              <Route path="/sites" element={<ManageSites />} />
              <Route path="/pms-voters" element={<ManagePmsVoters />} />
            </Route>

            {/* Superadmin only routes */}
            <Route element={<RoleGuard allowed={['superadmin']} />}>
              <Route path="/regular-users" element={<RegularUsers />} />
              <Route path="/users" element={<ManageUsers />} />
              <Route path="/audit-logs" element={<AuditLogs />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </ErrorBoundary>
  );
}
